import * as React from "react";
import { cn } from "../../lib/utils";
import { Label } from "./Label";

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, invalid, ...props }, ref) => (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        "w-full h-10 px-space-3 py-[10px]",
        // text-body (16px) below sm stops iOS Safari's focus auto-zoom;
        // ≥sm keeps the original text-small so desktop is pixel-identical.
        "font-body text-body sm:text-small text-onyx bg-white-pure",
        "border border-border-light rounded-md",
        "placeholder:text-gray-mid",
        "transition-colors duration-fast ease-standard",
        "focus:outline-none focus:border-gold focus:shadow-goldRing",
        "disabled:bg-white-warm disabled:opacity-60 disabled:cursor-not-allowed",
        invalid && "border-status-error focus:border-status-error",
        className
      )}
      {...props}
    />
  )
);
Input.displayName = "Input";

export interface FormFieldProps {
  label?: React.ReactNode;
  required?: boolean;
  helperText?: string;
  error?: string;
  htmlFor?: string;
  children: React.ReactNode;
  className?: string;
}

/**
 * Host elements a `<label for>` is allowed to point at — the HTML spec's
 * "labelable elements". Pointing `for` at anything else (a wrapper `<div>`,
 * say) is invalid HTML and still buys no accessible name, so for those children
 * we render exactly what we rendered before rather than inventing an id.
 */
const LABELABLE_HOST_ELEMENTS = new Set([
  "button",
  "input",
  "meter",
  "output",
  "progress",
  "select",
  "textarea",
]);

/** The subset of a control's props FormField reads or writes. */
type ControlProps = {
  id?: unknown;
  required?: unknown;
  "aria-describedby"?: unknown;
  "aria-required"?: unknown;
};

/**
 * Can control props (`id`, `aria-describedby`, `aria-required`) safely be
 * cloned onto this child?
 *
 * A composite element (`<Input />`, `<Select />`, a picker component, ...)
 * forwards its props to whatever control it renders, so we hand them over and
 * let them land; a component that ignores them leaves us exactly where we were.
 * Built-in symbol types (`<React.Fragment>`, `<React.Suspense>`) accept no
 * props at all and React warns when you clone any onto them, so they're out.
 */
function acceptsControlProps(child: React.ReactElement): boolean {
  // Widened to `unknown` deliberately: `forwardRef`/`memo` elements are objects
  // at runtime even though the React types describe every non-host type as a
  // function, so the narrower type would make the object branch unreachable.
  const type: unknown = child.type;
  if (typeof type === "string") return LABELABLE_HOST_ELEMENTS.has(type);
  return typeof type === "function" || (typeof type === "object" && type !== null);
}

/** Append to a caller's `aria-describedby` token list rather than replacing it. */
function mergeDescribedBy(existing: unknown, added: string): string {
  const base = typeof existing === "string" ? existing.trim() : "";
  if (!base) return added;
  return base.split(/\s+/).includes(added) ? base : `${base} ${added}`;
}

export function FormField({
  label,
  required,
  helperText,
  error,
  htmlFor,
  children,
  className,
}: FormFieldProps) {
  // Stable across renders and unique per FormField instance, so the ids derived
  // from it never collide even when the same field renders many times a page.
  const uid = React.useId();
  const helperTextId = `${uid}-helper`;
  const errorId = `${uid}-error`;

  // Only one of the two ever renders (see the JSX below), so only one can
  // describe the control — error wins, matching what's on screen.
  const describedById = error ? errorId : helperText ? helperTextId : undefined;

  const child = React.isValidElement<ControlProps>(children) ? children : null;
  const cloneable = child !== null && acceptsControlProps(child);
  // Only honoured for a child we'd have been willing to point at anyway —
  // adopting the id off a wrapper `<div>` would aim `for` at a non-labelable
  // element, which is invalid HTML and worse than the gap we're closing.
  const childId =
    cloneable && typeof child.props.id === "string" ? child.props.id : undefined;

  // Precedence: an explicit `htmlFor` always wins, then an id the caller put on
  // the control itself, and only failing both do we mint one — and only when
  // there's a label to point at it. When the caller supplied either we never
  // clone an `id`: theirs may belong to a control nested deeper than our direct
  // child, and duplicating it in the document would be worse than the gap
  // we're closing.
  const canMintId = cloneable && !htmlFor && !childId && Boolean(label);
  const controlId = htmlFor ?? childId ?? (canMintId ? uid : undefined);

  const control =
    child && cloneable
      ? React.cloneElement(child, {
          ...(canMintId ? { id: controlId } : null),
          ...(describedById
            ? {
                "aria-describedby": mergeDescribedBy(
                  child.props["aria-describedby"],
                  describedById
                ),
              }
            : null),
          // The visual asterisk is `aria-hidden`, so this is the only thing that
          // tells a screen reader the field is required. It's announce-only —
          // unlike `required`, it never blocks form submission. An explicit
          // `required` or `aria-required` on the control always wins.
          ...(required &&
          child.props.required === undefined &&
          child.props["aria-required"] === undefined
            ? { "aria-required": true }
            : null),
        })
      : children;

  return (
    <div className={cn("flex flex-col", className)}>
      {label && (
        <Label htmlFor={controlId} required={required}>
          {label}
        </Label>
      )}
      {control}
      {error ? (
        <p
          id={errorId}
          className="text-label text-status-error mt-[6px]"
          role="alert"
        >
          {error}
        </p>
      ) : helperText ? (
        <p id={helperTextId} className="text-label text-gray-mid mt-[6px]">
          {helperText}
        </p>
      ) : null}
    </div>
  );
}
